import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Check,
  GitFork,
  Heart,
  Images,
  Plus,
  RotateCcw,
  Search,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { PrivateImage } from './private-media.jsx';
import { matchesItemSearch } from './wardrobe-search.js';
import { MirrorWord } from './branding.jsx';

const NODE_POSITIONS = [
  { x: 20, y: 20 },
  { x: 11, y: 51 },
  { x: 21, y: 82 },
  { x: 79, y: 20 },
  { x: 89, y: 51 },
  { x: 78, y: 82 },
];

const LENSES = [
  { id: 'everything', label: 'Everything' },
  { id: 'history', label: 'Real history' },
  { id: 'untried', label: 'Never tried' },
  { id: 'clean', label: 'Clean now' },
  { id: 'value', label: 'Value' },
];

const ROLE_RULES = [
  { role: 'layer', pattern: /\b(coat|jacket|blazer|cardigan|shacket|overshirt|over shirt|cape|poncho|kimono|bolero|shrug|throw[ -]?over)\b/ },
  { role: 'one-piece', pattern: /\b(dress|jumpsuit|romper|overall|unitard|gown)\b/ },
  { role: 'bottom', pattern: /\b(pant|pants|trouser|trousers|jean|jeans|denim|shorts|skirt|legging|leggings|culotte|culottes)\b/ },
  { role: 'footwear', pattern: /\b(shoe|shoes|boot|boots|sneaker|sneakers|loafer|loafers|heel|heels|sandal|sandals|slipper|slippers)\b/ },
  { role: 'hosiery', pattern: /\b(sock|socks|tight|tights|stocking|stockings|hosiery)\b/ },
  { role: 'accessory', pattern: /\b(belt|bag|purse|hat|cap|scarf|tie|jewelry|jewellery|necklace|bracelet|watch|glasses|sunglasses|glove|gloves)\b/ },
  { role: 'top', pattern: /\b(shirt|tee|t-shirt|tshirt|top|blouse|sweater|knit|hoodie|sweatshirt|tank|camisole|polo|jersey|bodysuit|corset|bustier|vest)\b/ },
];

const COMPATIBLE_ROLES = {
  top: ['bottom', 'layer', 'footwear', 'hosiery', 'accessory', 'one-piece'],
  bottom: ['top', 'layer', 'footwear', 'hosiery', 'accessory'],
  layer: ['top', 'bottom', 'one-piece', 'footwear', 'accessory'],
  'one-piece': ['layer', 'footwear', 'hosiery', 'accessory', 'top'],
  footwear: ['top', 'bottom', 'one-piece', 'layer', 'hosiery', 'accessory'],
  hosiery: ['footwear', 'bottom', 'one-piece'],
  accessory: ['top', 'bottom', 'one-piece', 'layer', 'footwear'],
  unknown: ['top', 'bottom', 'layer', 'one-piece', 'footwear', 'hosiery', 'accessory'],
};

const SPECIAL_LAYER_PATTERN = /\b(crop|cropped|sheer|mesh|corset|bustier|vest|overshirt|over shirt|shacket|cardigan|bolero|shrug|kimono|throw[ -]?over)\b/;

const ROLE_GAPS = {
  top: [
    { role: 'bottom', label: 'Scan pants, shorts or a skirt' },
    { role: 'footwear', label: 'Scan shoes' },
    { role: 'hosiery', label: 'Scan socks or tights' },
    { role: 'layer', label: 'Scan a layer' },
  ],
  bottom: [
    { role: 'top', label: 'Scan a shirt or top' },
    { role: 'footwear', label: 'Scan shoes' },
    { role: 'hosiery', label: 'Scan socks or tights' },
    { role: 'layer', label: 'Scan a layer' },
  ],
  layer: [
    { role: 'top', label: 'Scan a base top' },
    { role: 'bottom', label: 'Scan pants, shorts or a skirt' },
    { role: 'footwear', label: 'Scan shoes' },
    { role: 'one-piece', label: 'Scan a dress or jumpsuit' },
  ],
  'one-piece': [
    { role: 'layer', label: 'Scan a layer' },
    { role: 'footwear', label: 'Scan shoes' },
    { role: 'hosiery', label: 'Scan socks or tights' },
    { role: 'accessory', label: 'Scan an accessory' },
  ],
  footwear: [
    { role: 'bottom', label: 'Scan pants, shorts or a skirt' },
    { role: 'top', label: 'Scan a shirt or top' },
    { role: 'hosiery', label: 'Scan socks or tights' },
    { role: 'one-piece', label: 'Scan a dress or jumpsuit' },
  ],
  hosiery: [
    { role: 'footwear', label: 'Scan shoes' },
    { role: 'bottom', label: 'Scan pants, shorts or a skirt' },
    { role: 'one-piece', label: 'Scan a dress or jumpsuit' },
  ],
  accessory: [
    { role: 'top', label: 'Scan a shirt or top' },
    { role: 'bottom', label: 'Scan pants, shorts or a skirt' },
    { role: 'one-piece', label: 'Scan a dress or jumpsuit' },
    { role: 'footwear', label: 'Scan shoes' },
  ],
  unknown: [
    { role: 'top', label: 'Scan a shirt or top' },
    { role: 'bottom', label: 'Scan pants, shorts or a skirt' },
    { role: 'footwear', label: 'Scan shoes' },
    { role: 'layer', label: 'Scan a layer' },
  ],
};

function itemLabel(item) {
  return item?.label || item?.details?.category || 'Untitled piece';
}

function normalizedTags(item) {
  return (item?.tags || []).map(tag => String(tag).trim()).filter(Boolean);
}

function itemRoleText(item) {
  return [item?.label, item?.details?.category, ...(item?.tags || [])].filter(Boolean).join(' ').toLowerCase();
}

function garmentRole(item) {
  const text = itemRoleText(item);
  return ROLE_RULES.find(rule => rule.pattern.test(text))?.role || 'unknown';
}

function isSpecialLayer(item) {
  return SPECIAL_LAYER_PATTERN.test(itemRoleText(item));
}

function isUsefulPair(center, candidate, realCount) {
  if (realCount > 0) return true;
  const centerRole = garmentRole(center);
  const candidateRole = garmentRole(candidate);
  if (centerRole === candidateRole) {
    if (centerRole === 'top') return isSpecialLayer(center) || isSpecialLayer(candidate);
    if (centerRole === 'layer') return isSpecialLayer(center) || isSpecialLayer(candidate);
    return false;
  }
  return (COMPATIBLE_ROLES[centerRole] || COMPATIBLE_ROLES.unknown).includes(candidateRole);
}

function selectDiverseRelations(relations, limit = 6) {
  const picked = [];
  const seenRoles = new Set();
  for (const relation of relations) {
    const role = garmentRole(relation.item);
    if (seenRoles.has(role)) continue;
    picked.push(relation);
    seenRoles.add(role);
    if (picked.length === limit) return picked;
  }
  for (const relation of relations) {
    if (picked.includes(relation)) continue;
    picked.push(relation);
    if (picked.length === limit) break;
  }
  return picked;
}

function sameValue(left, right, key) {
  const a = String(left?.details?.[key] || '').trim().toLowerCase();
  const b = String(right?.details?.[key] || '').trim().toLowerCase();
  return !!a && a === b;
}

function pairCount(entries, centerId, candidateId) {
  const center = String(centerId);
  const candidate = String(candidateId);
  return (entries || []).reduce((count, entry) => {
    const ids = new Set((entry.itemIds || []).map(String));
    return count + (ids.has(center) && ids.has(candidate) ? 1 : 0);
  }, 0);
}

function marketValue(item) {
  return item?.aiMarket?.currentMarketValue || item?.aiMarket?.aftermarketValue || '';
}

function relationshipFor(center, candidate, state) {
  const centerTags = normalizedTags(center);
  const candidateTags = new Set(normalizedTags(candidate).map(tag => tag.toLowerCase()));
  const sharedTags = centerTags.filter(tag => candidateTags.has(tag.toLowerCase()));
  const realCount = pairCount(state.mirror?.ootdHistory, center.id, candidate.id);
  const generatedCount = pairCount(state.mirror?.outfits, center.id, candidate.id);
  const sameColor = sameValue(center, candidate, 'color');
  const sameMaterial = sameValue(center, candidate, 'material');
  const sameCategory = sameValue(center, candidate, 'category');
  const value = marketValue(candidate);
  const score = (realCount * 100) + (generatedCount * 30) + (sharedTags.length * 14) + (sameColor ? 10 : 0) + (sameMaterial ? 7 : 0) + (sameCategory ? 3 : 0) + (candidate.intent?.wear ? 2 : 0);
  let evidence = 'Never tried';
  if (candidate.wearStatus === 'laundry') evidence = 'Laundry';
  else if (realCount) evidence = `${realCount} saved fit${realCount === 1 ? '' : 's'}`;
  else if (generatedCount) evidence = `${generatedCount} saved possibility`;
  else if (sharedTags.length) evidence = `Shared tag: ${sharedTags[0]}`;
  else if (sameColor) evidence = 'Same palette';
  else if (sameMaterial) evidence = 'Shared material';
  else if (value) evidence = value;
  return {
    item: candidate,
    realCount,
    generatedCount,
    sharedTags,
    sameColor,
    sameMaterial,
    value,
    score,
    kind: realCount ? 'real' : 'possible',
    evidence,
  };
}

function relationMatchesLens(relation, lens) {
  if (lens === 'history') return relation.realCount > 0;
  if (lens === 'untried') return relation.realCount === 0 && relation.generatedCount === 0;
  if (lens === 'clean') return relation.item.wearStatus === 'clean';
  if (lens === 'value') return !!relation.value;
  return true;
}

function FieldNode({ relation, position, selected, onToggle }) {
  const item = relation.item;
  return <button
    type="button"
    className={`mirror-field-node ${relation.kind} ${selected ? 'selected' : ''}`}
    style={{ '--field-x': `${position.x}%`, '--field-y': `${position.y}%` }}
    onClick={onToggle}
    aria-pressed={selected}
    aria-label={`${selected ? 'Remove' : 'Add'} ${itemLabel(item)} ${selected ? 'from' : 'to'} current thought. ${relation.evidence}`}
  >
    <span className="mirror-field-node-image"><PrivateImage source={item.imageData} mediaId={item.imageMediaId} fallback="./assets/silver-knit.webp" alt="" />{selected ? <i><Check /></i> : null}</span>
    <strong>{itemLabel(item)}</strong>
    <small>{relation.evidence}</small>
  </button>;
}

function FieldGap({ gap, position, openCapture }) {
  return <button
    type="button"
    className="mirror-field-gap"
    style={{ '--field-x': `${position.x}%`, '--field-y': `${position.y}%` }}
    onClick={openCapture}
    aria-label={`${gap.label}. Open capture.`}
  >
    <span><Plus /></span>
    <strong>{gap.label}</strong>
    <small>Nothing useful in this role yet</small>
  </button>;
}

function ThoughtItem({ item, onRemove }) {
  return <article className="mirror-thought-item">
    <PrivateImage source={item.imageData} mediaId={item.imageMediaId} fallback="./assets/silver-knit.webp" alt="" />
    <span><strong>{itemLabel(item)}</strong><small>{item.wearStatus === 'laundry' ? 'Laundry' : item.details?.color || item.details?.category || 'In this thought'}</small></span>
    {onRemove ? <button type="button" onClick={onRemove} aria-label={`Remove ${itemLabel(item)} from thought`}><X /></button> : <i><Check /></i>}
  </article>;
}

function EmptyField({ openCapture }) {
  return <main className="mirror-field-empty">
    <div><Images /><h1>The Field begins with two pieces.</h1><p>Capture what you own. <MirrorWord /> will reveal the history and possibility between them without choosing for you.</p><button type="button" onClick={openCapture}>Capture a piece <ArrowRight /></button></div>
  </main>;
}

export default function MirrorField({ state, items, openCapture, onOpenTryOn, onSaveOotd, onOpenItem }) {
  const availableItems = useMemo(() => items.filter(item => item.imageData || item.imageMediaId), [items]);
  const [centerId, setCenterId] = useState(() => localStorage.getItem('mirror_field_center') || '');
  const [selectedIds, setSelectedIds] = useState([]);
  const [lens, setLens] = useState('everything');
  const [query, setQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  const [colorFilter, setColorFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortMode, setSortMode] = useState('relevance');
  const [branches, setBranches] = useState([]);
  const deferredQuery = useDeferredValue(query);

  useEffect(() => {
    if (!availableItems.length) return;
    if (!availableItems.some(item => String(item.id) === String(centerId))) setCenterId(String(availableItems[0].id));
  }, [availableItems, centerId]);

  useEffect(() => {
    if (centerId) localStorage.setItem('mirror_field_center', String(centerId));
  }, [centerId]);

  useEffect(() => {
    const valid = new Set(availableItems.map(item => String(item.id)));
    setSelectedIds(current => current.filter(id => valid.has(String(id)) && String(id) !== String(centerId)));
  }, [availableItems, centerId]);

  if (availableItems.length < 2) return <EmptyField openCapture={openCapture} />;

  const center = availableItems.find(item => String(item.id) === String(centerId)) || availableItems[0];
  const sortedRelations = availableItems
    .filter(item => String(item.id) !== String(center.id))
    .map(item => relationshipFor(center, item, state))
    .filter(relation => isUsefulPair(center, relation.item, relation.realCount))
    .filter(relation => relationMatchesLens(relation, lens))
    .sort((left, right) => lens === 'value'
      ? String(right.value).localeCompare(String(left.value), undefined, { numeric: true })
      : right.score - left.score);
  const relations = selectDiverseRelations(sortedRelations);
  const representedRoles = new Set(relations.map(relation => garmentRole(relation.item)));
  const availableRoles = new Set(availableItems.filter(item => String(item.id) !== String(center.id)).map(garmentRole));
  const gaps = lens === 'everything'
    ? (ROLE_GAPS[garmentRole(center)] || ROLE_GAPS.unknown)
      .filter(gap => !representedRoles.has(gap.role) && !availableRoles.has(gap.role))
      .slice(0, Math.max(0, 6 - relations.length))
    : [];
  const selectedItems = selectedIds.map(id => availableItems.find(item => String(item.id) === String(id))).filter(Boolean);
  const thoughtItems = [center, ...selectedItems].slice(0, 5);
  const roleOptions = [...new Set(availableItems.map(garmentRole))].sort();
  const colorOptions = [...new Set(availableItems.map(item => String(item.details?.color || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const locationOptions = [...new Set(availableItems.map(item => String(item.location?.name || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const pickerItems = availableItems
    .filter(item => String(item.id) !== String(center.id) && matchesItemSearch(item, deferredQuery, ''))
    .filter(item => roleFilter === 'all' || garmentRole(item) === roleFilter)
    .filter(item => colorFilter === 'all' || String(item.details?.color || '') === colorFilter)
    .filter(item => locationFilter === 'all' || String(item.location?.name || '') === locationFilter)
    .filter(item => {
      if (statusFilter === 'clean') return item.wearStatus === 'clean';
      if (statusFilter === 'laundry') return item.wearStatus === 'laundry';
      if (statusFilter === 'work') return !!item.workwear;
      if (statusFilter === 'favorite') return !!item.intent?.wear;
      if (statusFilter === 'sell') return !!item.intent?.sell;
      return true;
    })
    .sort((left, right) => {
      if (sortMode === 'name') return itemLabel(left).localeCompare(itemLabel(right));
      if (sortMode === 'recent') return String(right.updatedAt || right.createdAt || '').localeCompare(String(left.updatedAt || left.createdAt || ''));
      if (sortMode === 'value') return marketValue(right).localeCompare(marketValue(left), undefined, { numeric: true });
      return relationshipFor(center, right, state).score - relationshipFor(center, left, state).score;
    })
    .slice(0, 16);

  const chooseCenter = item => {
    setCenterId(String(item.id));
    setSelectedIds([]);
    setQuery('');
    setPickerOpen(false);
  };
  const toggleItem = itemId => setSelectedIds(current => {
    const id = String(itemId);
    if (current.includes(id)) return current.filter(value => value !== id);
    return current.length >= 4 ? current : [...current, id];
  });
  const forkThought = () => {
    if (thoughtItems.length < 2) return;
    setBranches(current => [{ id: `${Date.now()}`, itemIds: thoughtItems.map(item => String(item.id)) }, ...current].slice(0, 3));
    setSelectedIds([]);
  };
  const restoreBranch = branch => setSelectedIds(branch.itemIds.filter(id => id !== String(center.id)).slice(0, 4));

  return <main className="mirror-field-page">
    <section className="mirror-field-stage">
      <header className="mirror-field-heading"><h1>See everything this piece knows.</h1></header>
      <div className="mirror-field-tools">
        <div className="mirror-field-picker">
          <label><Search /><input value={query} onFocus={() => setPickerOpen(true)} onChange={event => { setQuery(event.target.value); setPickerOpen(true); }} placeholder="Search names, tags, colors, brands, genres, locations" /></label>
          {pickerOpen ? <div className="mirror-field-results">
            <div className="mirror-field-results-heading"><strong>Choose the center</strong><button type="button" onClick={() => setPickerOpen(false)} aria-label="Close item search"><X /></button></div>
            <div className="mirror-field-picker-filters"><label><span>Role</span><select value={roleFilter} onChange={event => setRoleFilter(event.target.value)}><option value="all">All roles</option>{roleOptions.map(role => <option value={role} key={role}>{role}</option>)}</select></label><label><span>Color</span><select value={colorFilter} onChange={event => setColorFilter(event.target.value)}><option value="all">All colors</option>{colorOptions.map(color => <option value={color} key={color}>{color}</option>)}</select></label><label><span>Location</span><select value={locationFilter} onChange={event => setLocationFilter(event.target.value)}><option value="all">All locations</option>{locationOptions.map(location => <option value={location} key={location}>{location}</option>)}</select></label><label><span>Status</span><select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}><option value="all">Any status</option><option value="clean">Clean</option><option value="laundry">Laundry</option><option value="work">Work clothes</option><option value="favorite">Favorite</option><option value="sell">Sell list</option></select></label><label><span>Sort</span><select value={sortMode} onChange={event => setSortMode(event.target.value)}><option value="relevance">Relationship</option><option value="recent">Recent</option><option value="name">Name</option><option value="value">Value</option></select></label></div>
            {pickerItems.length ? pickerItems.map(item => <button type="button" key={item.id} onClick={() => chooseCenter(item)}><PrivateImage source={item.imageData} mediaId={item.imageMediaId} fallback="./assets/silver-knit.webp" alt="" /><span><strong>{itemLabel(item)}</strong><small>{item.details?.brand || item.details?.category || item.location?.name || 'Wardrobe piece'}</small></span><ArrowRight /></button>) : <p>No pieces match that search.</p>}
          </div> : null}
        </div>
        <div className="mirror-field-lenses" aria-label="Field lenses"><span>Lens</span>{LENSES.map(entry => <button type="button" key={entry.id} className={lens === entry.id ? 'active' : ''} onClick={() => setLens(entry.id)}>{entry.label}</button>)}</div>
      </div>
      <section className="mirror-field-canvas" aria-label={`Relationships around ${itemLabel(center)}`}>
        <svg className="mirror-field-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">{relations.map((relation, index) => <line key={relation.item.id} x1="50" y1="51" x2={NODE_POSITIONS[index].x} y2={NODE_POSITIONS[index].y} className={relation.kind} />)}</svg>
        <button type="button" className="mirror-field-center" onClick={() => onOpenItem?.(center)} aria-label={`Open ${itemLabel(center)} details`}>
          <span><PrivateImage source={center.imageData} mediaId={center.imageMediaId} fallback="./assets/silver-knit.webp" alt={itemLabel(center)} /></span>
          <strong>{itemLabel(center)}</strong>
          <small>{center.wearStatus === 'laundry' ? 'Laundry' : `${garmentRole(center) === 'unknown' ? 'Piece' : garmentRole(center)} · Center of the Field`}</small>
        </button>
        {relations.map((relation, index) => <FieldNode key={relation.item.id} relation={relation} position={NODE_POSITIONS[index]} selected={selectedIds.includes(String(relation.item.id))} onToggle={() => toggleItem(relation.item.id)} />)}
        {gaps.map((gap, index) => <FieldGap key={gap.role} gap={gap} position={NODE_POSITIONS[relations.length + index]} openCapture={openCapture} />)}
        {!relations.length && !gaps.length ? <div className="mirror-field-no-relations"><Sparkles /><strong>No useful pairings in this lens.</strong><span>Change the lens or place another piece in the center.</span></div> : null}
        <p className="mirror-field-instruction">Select pieces to hold a thought. <MirrorWord /> shows the evidence; you decide what it means.</p>
        <div className="mirror-field-legend"><span><i /> Real history</span><span><i className="possible" /> Possible</span></div>
      </section>
    </section>
    <aside className="mirror-field-inspector" aria-label="Current thought">
      <div className="mirror-thought-heading"><div><h2>Current thought</h2><span>{thoughtItems.length} piece{thoughtItems.length === 1 ? '' : 's'}</span></div><Heart /></div>
      <div className="mirror-thought-list">{thoughtItems.map((item, index) => <ThoughtItem key={item.id} item={item} onRemove={index ? () => toggleItem(item.id) : null} />)}</div>
      <div className="mirror-thought-actions">
        <button type="button" className="primary" disabled={!thoughtItems.length} onClick={() => onOpenTryOn?.(thoughtItems.map(item => item.id))}><Images /> Open in Try On</button>
        <button type="button" className="primary" disabled={thoughtItems.length < 2} onClick={() => onSaveOotd?.(thoughtItems.map(item => item.id))}><Heart /> Save as OoTD</button>
        <button type="button" disabled={thoughtItems.length < 2} onClick={forkThought}><GitFork /> Fork</button>
        <button type="button" disabled={!selectedIds.length} onClick={() => setSelectedIds([])}><Trash2 /> Clear</button>
      </div>
      {branches.length ? <section className="mirror-field-branches"><div><strong>Held branches</strong><button type="button" onClick={() => setBranches([])}><RotateCcw /> Reset</button></div>{branches.map((branch, index) => <button type="button" key={branch.id} onClick={() => restoreBranch(branch)}><span>Branch {branches.length - index}</span><small>{branch.itemIds.length} pieces</small><ArrowRight /></button>)}</section> : null}
      <p className="mirror-field-truth"><span /> Solid connections come only from real saved OoTD history. Dotted connections are possibilities.</p>
    </aside>
  </main>;
}
